// Consent scope model — owner-controlled, per-linked-adult, granular and
// revocable, as locked by docs/revamp/05_PORTAL_DESIGN.md (§ "Sharing &
// consent", R3-6) and decided in docs/revamp/03_BACKEND_AND_PROFESSIONAL_LAYER.md
// Q3.6 (2026-06-17): "immediate cut + purge of shared copies; owner keeps local."
//
// The rules this module encodes — the same ones the guard tests assert:
//   • The OWNER alone controls consent. Any non-owner actor attempting grant /
//     revoke throws: a viewer can never self-escalate their own scope.
//   • Scopes are granular and per-linked-adult: an owner grants/revokes one
//     scope to one adult at a time.
//   • Revocation is IMMEDIATE — the returned state has the scope gone at once;
//     there is no retention window for the viewer.
//   • `revokeAll` clears every scope for one adult AND returns the purge intent:
//     cut access now + purge the shared copies, while the owner keeps local data.
//
// Pure TS, no dependencies. State is immutable: every mutator returns a fresh
// `ConsentState`; the input is never modified.

import type { ConsentScopeKey, Id } from "./types";

/**
 * The exhaustive, canonical set of consent scopes an owner can grant per adult.
 * Mirrors `ConsentScopeKey` in `./types`:
 *   - `progress`  → shared progress / engagement summaries.
 *   - `results`   → assigned-practice results flowing back to the viewer.
 *   - `export`    → permission to export a progress PDF for the child.
 */
export const CONSENT_SCOPES: readonly ConsentScopeKey[] = [
  "progress",
  "results",
  "export",
];

/**
 * Consent for a single child. The owner holds full scope implicitly (never
 * stored); `grants` records exactly the scopes the owner has shared with each
 * OTHER (viewer) account. An account absent from `grants`, or present with an
 * empty list, sees nothing.
 */
export interface ConsentState {
  childId: Id;
  ownerAccountId: Id;
  /** viewer accountId → the scopes the owner has granted that viewer. */
  readonly grants: Readonly<Record<Id, readonly ConsentScopeKey[]>>;
}

/**
 * The side-effect intent returned by {@link revokeAll}. It is a declarative
 * instruction for the storage layer (Edge Function / RPC), not an action taken
 * here. All three flags are `true` by construction — revocation has exactly one
 * semantics (Q3.6): cut access immediately, purge the viewer's shared copies,
 * and keep the owner's own local data.
 */
export interface PurgeIntent {
  childId: Id;
  /** The viewer whose access is being fully revoked. */
  adultId: Id;
  /** Access is cut now — no retention window for the viewer. */
  readonly cutAccess: true;
  /** Aggregates shared to the viewer are deleted. */
  readonly purgeSharedCopies: true;
  /** The owner's local data is untouched. */
  readonly keepOwnerLocal: true;
  /** When the cut takes effect — always immediate. */
  readonly effective: "immediate";
  /** The scopes that were in force at revoke time (for the audit log). */
  revokedScopes: ConsentScopeKey[];
}

/** Result of {@link revokeAll}: the cleared state plus the purge instruction. */
export interface RevokeAllResult {
  state: ConsentState;
  purge: PurgeIntent;
}

/** A fresh consent state for a child with no scopes shared to anyone yet. */
export function createConsentState(
  childId: Id,
  ownerAccountId: Id,
): ConsentState {
  return { childId, ownerAccountId, grants: {} };
}

/** True when `accountId` is the owner of this child's consent. */
export function isOwner(state: ConsentState, accountId: Id): boolean {
  return state.ownerAccountId === accountId;
}

/**
 * Guard: only the owner may change consent. Throws otherwise so a viewer can
 * never silently grant themselves (or anyone) a scope.
 */
function assertOwnerActor(state: ConsentState, actorId: Id): void {
  if (!isOwner(state, actorId)) {
    throw new Error(
      "Only the child's owner can change consent — viewers cannot self-escalate.",
    );
  }
}

/** The scopes currently granted to `adultId` (a fresh array; never the owner). */
function grantedScopes(
  state: ConsentState,
  adultId: Id,
): ConsentScopeKey[] {
  return [...(state.grants[adultId] ?? [])];
}

/** Replace one adult's scope list, returning a fresh state. */
function withGrants(
  state: ConsentState,
  adultId: Id,
  scopes: readonly ConsentScopeKey[],
): ConsentState {
  const grants: Record<Id, readonly ConsentScopeKey[]> = { ...state.grants };
  if (scopes.length === 0) {
    delete grants[adultId];
  } else {
    // Keep canonical scope order so equal grants compare equal.
    grants[adultId] = CONSENT_SCOPES.filter((s) => scopes.includes(s));
  }
  return { ...state, grants };
}

/**
 * Owner grants one scope to one viewer. Idempotent (re-granting is a no-op).
 * The owner can never be a grant target — owners already hold full scope.
 * Throws if `actorId` is not the owner.
 */
export function grant(
  state: ConsentState,
  actorId: Id,
  adultId: Id,
  scope: ConsentScopeKey,
): ConsentState {
  assertOwnerActor(state, actorId);
  if (isOwner(state, adultId)) {
    // No-op: the owner's full scope is implicit, never stored.
    return state;
  }
  const current = grantedScopes(state, adultId);
  if (current.includes(scope)) {
    return state;
  }
  return withGrants(state, adultId, [...current, scope]);
}

/**
 * Owner revokes one scope from one viewer. The effect is IMMEDIATE — the
 * returned state no longer carries the scope. Revoking a scope the viewer never
 * had is a no-op. Throws if `actorId` is not the owner.
 */
export function revoke(
  state: ConsentState,
  actorId: Id,
  adultId: Id,
  scope: ConsentScopeKey,
): ConsentState {
  assertOwnerActor(state, actorId);
  const current = grantedScopes(state, adultId);
  if (!current.includes(scope)) {
    return state;
  }
  return withGrants(
    state,
    adultId,
    current.filter((s) => s !== scope),
  );
}

/**
 * Whether `adultId` may currently see `scope`. The owner always can (implicit
 * full scope); any other account can only see a scope the owner has explicitly
 * granted — there is no path for a viewer to see more than they were given.
 */
export function canViewerSee(
  state: ConsentState,
  adultId: Id,
  scope: ConsentScopeKey,
): boolean {
  if (isOwner(state, adultId)) {
    return true;
  }
  return (state.grants[adultId] ?? []).includes(scope);
}

/**
 * Owner revokes ALL scopes from one viewer at once. Returns the cleared state
 * plus the {@link PurgeIntent} the storage layer must act on: immediate access
 * cut + purge of the viewer's shared copies, owner's local data kept. Throws if
 * `actorId` is not the owner.
 */
export function revokeAll(
  state: ConsentState,
  actorId: Id,
  adultId: Id,
): RevokeAllResult {
  assertOwnerActor(state, actorId);
  const revokedScopes = grantedScopes(state, adultId);
  return {
    state: withGrants(state, adultId, []),
    purge: {
      childId: state.childId,
      adultId,
      cutAccess: true,
      purgeSharedCopies: true,
      keepOwnerLocal: true,
      effective: "immediate",
      revokedScopes,
    },
  };
}
